import { useMemo } from "react";
import { DateTime } from "luxon";

import { CategorizedBuyerLists, useBuyerLists } from "lib/hooks/api/buyer_lists/useBuyerLists";
import { useOrganisations } from "lib/hooks/api/organisations/useOrganisations";
import { SpendFilters } from "lib/hooks/api/spend/spendFilters";
import { SearchOrgRole } from "lib/types/graphQLEnums";
import { SpendDataFilters } from "./useSpendUrlState";

/**
 * Sorted, not just deduped: this array reaches the query key and everything else keyed on
 * the filters, so the same set of buyers arriving in a different order from the API would
 * otherwise read as a different search.
 */
function sortedUnique(guids: string[]): string[] {
  return [...new Set(guids)].sort();
}

/**
 * Turns the page's filters into the API's shape. Buyer lists and buyer categories are
 * expanded into buyer guids here because the spend API only filters buyers by guid.
 *
 * The supplier selection is passed through as it stands, including the all-competitors and
 * all-partners tokens: app-service expands those from the caller's signal settings, the
 * same as notice search.
 */
export function getSpendDataFiltersForApi(
  values: SpendDataFilters,
  buyerLists?: CategorizedBuyerLists,
  matchingBuyerGuidsForCategories?: string[],
): SpendFilters {
  const filter: SpendFilters = {};

  if (values.buyers) {
    filter.buyerGuids = values.buyers;
  }

  if (values.buyerLists && buyerLists) {
    let buyerGuids: string[] = filter.buyerGuids || [];
    for (const id of values.buyerLists) {
      const list = buyerLists.all.find((list) => list.id === id);
      if (list) {
        buyerGuids = [...buyerGuids, ...list.entries.map((e) => e.id)];
      }
    }

    filter.buyerGuids = sortedUnique(buyerGuids);
  }

  if (values.buyerCategories && matchingBuyerGuidsForCategories) {
    const updatedBuyerGuids = [...matchingBuyerGuidsForCategories];

    if (filter.buyerGuids) {
      updatedBuyerGuids.push(...filter.buyerGuids);
    }
    filter.buyerGuids = sortedUnique(updatedBuyerGuids);
  }

  // Resolved to buyers by app-service, which narrows the buyer guids above to those inside it.
  if (values.buyerCountryRegions?.countries?.length) {
    filter.buyerCountryCodes = sortedUnique(values.buyerCountryRegions.countries);
  }
  if (values.buyerCountryRegions?.regions?.length) {
    filter.buyerRegionIds = sortedUnique(values.buyerCountryRegions.regions);
  }

  if (values.supplierGuids) {
    filter.supplierGuids = values.supplierGuids;
  }

  if (values.dateRange) {
    // The spend API validates dates strictly as ISO YYYY-MM-DD; the stored range
    // holds full ISO datetimes, so truncate to the date part.
    filter.dateFrom = values.dateRange.from
      ? DateTime.fromISO(values.dateRange.from).toISODate() ?? undefined
      : undefined;
    filter.dateTo = values.dateRange.to
      ? DateTime.fromISO(values.dateRange.to).toISODate() ?? undefined
      : undefined;
  }

  return filter;
}

/** The API-shaped filters for the current page filters, with lists and categories resolved. */
export function useSpendApiFilters(filters: SpendDataFilters): SpendFilters {
  const { data: buyerLists } = useBuyerLists();
  const { data: buyerSearch } = useOrganisations(
    {
      // The spend API takes buyer guids only, so categories are resolved to guids here.
      // 1000 is the API's page cap; a category with more buyers than that is truncated.
      limit: 1000,
      page: 1,
      role: SearchOrgRole.Buyer,
      pubSecOrgTypes: filters.buyerCategories,
    },
    { enabled: !!filters.buyerCategories },
  );

  // Memoised on the query result: mapped inline, this is a new array every render, which
  // makes the filters below look new every render too and everything keyed on them re-run.
  const matchingBuyerGuidsForCategories = useMemo(
    () => buyerSearch?.searchOrganisations.orgs.map((buyer) => buyer.id),
    [buyerSearch],
  );

  return useMemo(
    () => getSpendDataFiltersForApi(filters, buyerLists, matchingBuyerGuidsForCategories),
    [filters, buyerLists, matchingBuyerGuidsForCategories],
  );
}
